import { TextField } from "@platform/design-system";
import { IconButton, Stack } from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";

import type { ReactElement } from "react";

/** A single age-based amount band: `amount` for ages `ageLower`–`ageUpper`. */
export interface AgeBasedAmountEntry {
  amount: number;
  ageLower: number;
  ageUpper: number;
}

/** The draft (string-backed) form of a band while it is being edited. */
export interface DraftBand {
  /** Stable key for React, since bands can be added and removed. */
  id: string;
  amount: string;
  ageLower: string;
  ageUpper: string;
}

export const makeDraftBand = (
  amount = "",
  ageLower = "",
  ageUpper = "",
): DraftBand => ({
  id: crypto.randomUUID(),
  amount,
  ageLower,
  ageUpper,
});

export const toDraftBands = (bands: AgeBasedAmountEntry[]): DraftBand[] =>
  bands.length > 0
    ? bands.map((b) =>
        makeDraftBand(String(b.amount), String(b.ageLower), String(b.ageUpper)),
      )
    : [makeDraftBand()];

export const isValidBand = (band: DraftBand): boolean => {
  const amount = Number(band.amount);
  const ageLower = Number(band.ageLower);
  const ageUpper = Number(band.ageUpper);

  return (
    band.amount !== "" &&
    band.ageLower !== "" &&
    band.ageUpper !== "" &&
    Number.isFinite(amount) &&
    Number.isInteger(ageLower) &&
    Number.isInteger(ageUpper) &&
    amount >= 0 &&
    ageLower >= 0 &&
    ageLower <= 120 &&
    ageUpper >= 0 &&
    ageUpper <= 120
  );
};

interface AgeBandAmountsFieldProps {
  bands: DraftBand[];
  showErrors: boolean;
  onUpdate: (index: number, field: keyof DraftBand, value: string) => void;
  onRemove: (index: number) => void;
}

/**
 * Controlled list of guarantee-issue age-based amounts (`$X for ages Y–Z`) with
 * add/remove. Rendered by `GuaranteeIssueCellEditor` when the `ageBasedAmounts`
 * type is selected.
 */
export const AgeBandAmountsField = ({
  bands,
  showErrors,
  onUpdate,
  onRemove,
}: AgeBandAmountsFieldProps): ReactElement => (
  <>
    {bands.map((band, index) => {
      const invalid = showErrors && !isValidBand(band);

      return (
        <Stack key={band.id} direction="row" spacing={1} alignItems="center">
          <TextField
            size="sm"
            type="number"
            label="Amount"
            value={band.amount}
            onChange={(e) => onUpdate(index, "amount", e.target.value)}
            error={invalid}
            inputProps={{ min: 0 }}
            fullWidth
          />

          <TextField
            size="sm"
            type="number"
            label="Age lower"
            value={band.ageLower}
            onChange={(e) => onUpdate(index, "ageLower", e.target.value)}
            error={invalid}
            inputProps={{ min: 0, max: 120, step: 1 }}
            fullWidth
          />

          <TextField
            size="sm"
            type="number"
            label="Age upper"
            value={band.ageUpper}
            onChange={(e) => onUpdate(index, "ageUpper", e.target.value)}
            error={invalid}
            inputProps={{ min: 0, max: 120, step: 1 }}
            fullWidth
          />

          <IconButton
            size="small"
            aria-label="Remove age band"
            onClick={() => onRemove(index)}
            disabled={bands.length === 1}
          >
            <CloseOutlinedIcon fontSize="small" />
          </IconButton>
        </Stack>
      );
    })}
  </>
);
